import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Mic, Eye, Zap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  {
    icon: Mic,
    title: "Listens in Real Time",
    desc: "AI captures and transcribes the interviewer's questions the instant they're spoken — zero lag.",
  },
  {
    icon: Eye,
    title: "Invisible to Callers",
    desc: "Hints appear only on your screen. No audio output, no detection, no disruption to the call.",
  },
  {
    icon: Zap,
    title: "Under-1-Second Answers",
    desc: "Context-aware responses surface before you've even finished hearing the question.",
  },
];

const TEASER_STATS = [
  { value: "2,400+", label: "On the waitlist" },
  { value: "<1s",    label: "Response latency" },
  { value: "97%",    label: "Accuracy target" },
];

function useCountdown(targetDate) {
  const calc = () => {
    const diff = targetDate - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0 };
    return {
      days:  Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      mins:  Math.floor((diff % 3600000) / 60000),
      secs:  Math.floor((diff % 60000) / 1000),
    };
  };
  const [tick, setTick] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTick(calc()), 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return tick;
}

export default function PhoneInterviewComingSoon() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // 45 days from now — fixed at mount
  const [launchDate] = useState(() => Date.now() + 45 * 86400000);
  const { days, hours, mins, secs } = useCountdown(launchDate);

  const handleWaitlist = (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSubmitted(true);
    toast.success("You're on the waitlist!");
  };

  return (
    <>
      {/* Keyframe styles */}
      <style>{`
        @keyframes sonar {
          0%   { transform: scale(0.85); opacity: 0.6; }
          100% { transform: scale(2.8);  opacity: 0; }
        }
        @keyframes orbit-cw {
          from { transform: rotate(0deg)   translateX(84px) rotate(0deg); }
          to   { transform: rotate(360deg) translateX(84px) rotate(-360deg); }
        }
        @keyframes orbit-ccw {
          from { transform: rotate(0deg)    translateX(64px) rotate(0deg); }
          to   { transform: rotate(-360deg) translateX(64px) rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0);   }
          50%       { transform: translateY(-8px); }
        }
        @keyframes glow-pulse {
          0%, 100% { opacity: 0.35; }
          50%       { opacity: 0.65; }
        }
        @keyframes scanline {
          0%   { transform: translateY(-100%); opacity: 0; }
          10%  { opacity: 0.4; }
          90%  { opacity: 0.4; }
          100% { transform: translateY(100vh); opacity: 0; }
        }
        .sonar-ring { animation: sonar 3s ease-out infinite; }
        .sonar-ring:nth-child(2) { animation-delay: 1s; }
        .sonar-ring:nth-child(3) { animation-delay: 2s; }
        .orbit-dot-1 { animation: orbit-cw  9s linear infinite; }
        .orbit-dot-2 { animation: orbit-cw  9s linear infinite; animation-delay: -3s; }
        .orbit-dot-3 { animation: orbit-cw  9s linear infinite; animation-delay: -6s; }
        .orbit-dot-4 { animation: orbit-ccw 13s linear infinite; }
        .orbit-dot-5 { animation: orbit-ccw 13s linear infinite; animation-delay: -6.5s; }
        .phone-float { animation: float 4s ease-in-out infinite; }
        .bg-glow    { animation: glow-pulse 4s ease-in-out infinite; }
        .scanline   { animation: scanline 8s linear infinite; animation-delay: 2s; }
        .ticker-num { font-variant-numeric: tabular-nums; }
      `}</style>

      <div className="min-h-screen bg-[#030303] text-white flex flex-col relative overflow-hidden">

        {/* ── Background layers ── */}
        {/* Grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }} />
        {/* Central radial glow */}
        <div className="absolute inset-0 pointer-events-none bg-glow" style={{
          background: "radial-gradient(ellipse 55% 45% at 50% 38%, rgba(0,85,255,0.14) 0%, transparent 70%)",
        }} />
        {/* Vertical accent line */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-48 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, rgba(0,85,255,0.5), transparent)" }} />
        {/* Scanline */}
        <div className="scanline absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#0055FF]/20 to-transparent pointer-events-none" />
        {/* Corner vignettes */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 100% 100% at 50% 50%, transparent 40%, rgba(3,3,3,0.9) 100%)",
        }} />

        {/* ── Header ── */}
        <header className="relative z-10 flex items-center justify-between px-8 py-5 border-b border-white/[0.04]">
          <button onClick={() => nav("/app")}
            className="flex items-center gap-2 text-sm text-neutral-600 hover:text-white transition-colors group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            Dashboard
          </button>
          <div className="flex items-center gap-2">
            <img src="/wingman-logo.png" alt="Wingman.AI" className="h-7 w-auto" />
            <span className="font-display font-black tracking-tight text-sm">
              WINGMAN<span className="text-[#D3FF24]">.</span>AI
            </span>
          </div>
          <div className="w-24" /> {/* spacer */}
        </header>

        {/* ── Hero ── */}
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 py-16">

          {/* Status badge */}
          <div className="flex items-center gap-2.5 border border-[#D3FF24]/25 bg-[#D3FF24]/[0.04] px-4 py-2 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D3FF24] animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#D3FF24]">Coming Soon</span>
          </div>

          {/* Large Coming Soon text */}
          <div className="mb-6 select-none pointer-events-none">
            <p className="font-display font-black uppercase tracking-[0.2em] text-transparent"
              style={{
                fontSize: "clamp(3.5rem, 10vw, 7rem)",
                WebkitTextStroke: "1px rgba(255,255,255,0.07)",
                lineHeight: 1,
              }}>
              Coming Soon
            </p>
          </div>

          {/* Phone visual */}
          <div className="relative w-48 h-48 flex items-center justify-center mb-16 phone-float">
            {/* Sonar rings */}
            <div className="sonar-ring absolute w-24 h-24 rounded-full border border-[#0055FF]/40" />
            <div className="sonar-ring absolute w-24 h-24 rounded-full border border-[#0055FF]/30" />
            <div className="sonar-ring absolute w-24 h-24 rounded-full border border-[#0055FF]/20" />

            {/* Static outer rings */}
            <div className="absolute w-44 h-44 rounded-full border border-white/[0.04]" />
            <div className="absolute w-32 h-32 rounded-full border border-white/[0.06]" />

            {/* Orbiting particles — clockwise */}
            {[0, 1, 2].map(i => (
              <div key={`cw-${i}`} className={`absolute w-full h-full orbit-dot-${i + 1}`}
                style={{ top: 0, left: 0 }}>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-[#D3FF24] rounded-full shadow-[0_0_6px_#D3FF24]" />
              </div>
            ))}

            {/* Orbiting particles — counter-clockwise */}
            {[4, 5].map(i => (
              <div key={`ccw-${i}`} className={`absolute w-full h-full orbit-dot-${i}`}
                style={{ top: 0, left: 0 }}>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-[#0055FF] rounded-full shadow-[0_0_6px_#0055FF]" />
              </div>
            ))}

            {/* Core */}
            <div className="relative z-10 w-[72px] h-[72px] rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #0055FF 0%, #003ACC 100%)",
                boxShadow: "0 0 0 1px rgba(0,85,255,0.4), 0 0 40px rgba(0,85,255,0.5), 0 0 80px rgba(0,85,255,0.2)",
              }}>
              <Phone className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
          </div>

          {/* Headline */}
          <h1 className="font-display font-black tracking-tighter leading-[0.9] mb-6"
            style={{ fontSize: "clamp(3rem, 8vw, 5.5rem)" }}>
            Your AI Wingman,
            <br />
            <span style={{
              backgroundImage: "linear-gradient(110deg, #0055FF 0%, #4488FF 45%, #D3FF24 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
              On The Phone.
            </span>
          </h1>

          <p className="text-neutral-400 text-lg max-w-lg leading-relaxed mb-3">
            Real-time AI coaching during live phone interviews.
            Hear the question. See the answer. Instantly.
          </p>
          <p className="text-neutral-700 text-sm mb-14 font-mono">
            Works on any call — mobile, VoIP, video — invisibly.
          </p>

          {/* Countdown */}
          <div className="flex items-start gap-3 mb-16">
            {[["Days", days], ["Hours", hours], ["Mins", mins], ["Secs", secs]].map(([label, val]) => (
              <div key={label} className="flex flex-col items-center">
                <div className="relative w-18 px-5 py-4 border border-white/[0.07] bg-white/[0.02] overflow-hidden"
                  style={{ minWidth: "72px" }}>
                  {/* inner top line */}
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#0055FF]/30 to-transparent" />
                  <div className="ticker-num text-3xl font-black text-center tabular-nums">
                    {String(val).padStart(2, "0")}
                  </div>
                </div>
                <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-700 mt-2">{label}</div>
              </div>
            ))}
          </div>

          {/* Stats strip */}
          <div className="flex items-center gap-10 mb-16">
            {TEASER_STATS.map((s, i) => (
              <React.Fragment key={s.label}>
                <div className="text-center">
                  <div className="text-2xl font-black text-white">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-0.5">{s.label}</div>
                </div>
                {i < TEASER_STATS.length - 1 && <div className="w-px h-8 bg-white/[0.06]" />}
              </React.Fragment>
            ))}
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-3 gap-4 max-w-2xl mb-16">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title}
                className="border border-white/[0.06] bg-white/[0.01] p-5 text-left relative overflow-hidden group hover:border-white/15 transition-colors">
                {/* Corner accent */}
                <div className="absolute top-0 right-0 w-8 h-8 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: "linear-gradient(225deg, rgba(0,85,255,0.15), transparent)" }} />
                <div className="w-8 h-8 border border-[#D3FF24]/20 bg-[#D3FF24]/5 flex items-center justify-center mb-4">
                  <Icon className="w-4 h-4 text-[#D3FF24]" strokeWidth={1.75} />
                </div>
                <div className="text-sm font-semibold text-white mb-1.5">{title}</div>
                <div className="text-xs text-neutral-500 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>

          {/* Waitlist form */}
          {!submitted ? (
            <div className="w-full max-w-sm">
              <p className="text-xs text-neutral-600 mb-3 tracking-wide">
                Get early access — be first when it launches
              </p>
              <form onSubmit={handleWaitlist} className="flex">
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 bg-white/[0.04] border border-white/10 border-r-0 px-4 py-3 text-sm outline-none focus:border-[#0055FF]/50 placeholder:text-neutral-700 transition-colors"
                />
                <button type="submit"
                  className="bg-[#0055FF] text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#0044CC] transition-colors border border-[#0055FF] shrink-0">
                  Notify Me
                </button>
              </form>
              <p className="text-[10px] text-neutral-700 mt-2">
                No spam. One email when we launch.
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-3 border border-[#D3FF24]/25 bg-[#D3FF24]/[0.04] px-6 py-4">
              <CheckCircle2 className="w-4 h-4 text-[#D3FF24] shrink-0" strokeWidth={1.75} />
              <div className="text-sm">
                <span className="text-[#D3FF24] font-semibold">You're on the list.</span>
                <span className="text-neutral-400 ml-2">We'll reach out at launch.</span>
              </div>
            </div>
          )}

        </main>

        {/* ── Footer ── */}
        <footer className="relative z-10 border-t border-white/[0.04] px-8 py-4 flex items-center justify-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-700">
            WINGMAN.AI · Phone Interview · In Development
          </p>
        </footer>

      </div>
    </>
  );
}
